import { Close, Content, Overlay, Portal, Root } from '@radix-ui/react-dialog';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@/components/elements/Button';
import { Form } from '@/components/elements/Form';
import RichText from '@/components/elements/RichText';
import { TabFields, UploadTabFields } from '@/components/elements/TabFields';
import { toast } from '@/components/elements/Toast/useToast';
import Container from '@/components/global/Container';
import { Icon } from '@/components/global/Icons';
import CircleTick from '@/icons/CircleTick';
import { I18nLocales, ILocales } from '@/interfaces/locales.interfaces';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpChunk } from '@mux/upchunk';
import { useRouter } from 'next/router';
import { z } from 'zod';
import { IStarStoryFormTabs, IStarStoryFormTabsTitles } from './StarStoryForm';

//Max image size to upload for the form is 10MB
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
//Max video size to upload for the form is 320MB
export const MAX_VIDEO_SIZE = 320 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic'];
export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/avi', 'video/quicktime', '.mov'];

export type FileObject = { file: File; error: boolean };

const customFileObject = z.object({
    file: z.custom<File>(),
    error: z.boolean(),
});

export const StarStoryFormSchema = z.object({
    firstName: z.string().trim().min(1, ' '),
    lastName: z.string().trim().min(1, ' '),
    email: z.string().email().min(1, ' '),
    roadTripReason: z.string().min(1, ' '),
    travelPartners: z.string().min(1, ' '),
    whereAndWhy: z.string().trim().min(1, ' '),
    favouriteExperience: z.string().trim().min(1, ' '),
    madeYouFeel: z.string().trim().min(1, ' '),
    terms: z.boolean().refine((value) => value === true),
    updatesAndDeals: z.boolean().optional(),
    images: z
        .array(customFileObject)
        .refine(
            (fileObject) => {
                return fileObject.every((files) => files.file instanceof File);
            },
            {
                message: 'Expected a file',
            }
        )
        .refine((fileObject) => fileObject.every((files) => files.file.size <= MAX_IMAGE_SIZE), `File size should be less than 10mb.`)
        .refine((fileObject) => fileObject.every((files) => ACCEPTED_IMAGE_TYPES.includes(files.file.type)), 'Only these types are allowed .jpeg, .png and .heic')
        .refine((fileObject) => fileObject.every((files) => !files.error))
        .optional(),

    videos: z
        .array(customFileObject)
        .refine((fileObject) => fileObject.every((files) => files.file.size <= MAX_VIDEO_SIZE), `File size should be less than 320mb.`)
        .refine((fileObject) => fileObject.every((files) => ACCEPTED_VIDEO_TYPES.includes(files.file.type)), 'Only these types are allowed .mp4, .avi and .mov')
        .optional(),
    videos_mux_asset_ids: z.string().array().optional(),
    videos_mux_playback_ids: z.string().array().optional(),
});
export type StarStoryFormData = z.infer<typeof StarStoryFormSchema>;

export const StarStoryFormTabContent = ({
    tabItems,
    termsAndConditions,
    setSubmissionSuccess,
}: {
    tabItems: IStarStoryFormTabs[];
    termsAndConditions?: string;
    submissionSuccess?: boolean;
    setSubmissionSuccess: (value: boolean) => void;
}) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [controller, setController] = useState(new AbortController());
    const router = useRouter();

    useEffect(() => {
        setController(new AbortController());
        return () => controller.abort();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const form = useForm<StarStoryFormData>({
        reValidateMode: 'onBlur',
        mode: 'onBlur',
        resolver: zodResolver(StarStoryFormSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            roadTripReason: '',
            travelPartners: '',
            whereAndWhy: '',
            favouriteExperience: '',
            madeYouFeel: '',
            terms: undefined,
            updatesAndDeals: undefined,
            images: [] as FileObject[],
            videos: [] as FileObject[],
            videos_mux_asset_ids: [],
            videos_mux_playback_ids: [],
        },
    });

    const { errors } = form.formState;

    const [activeTab, setActiveTab] = useState<IStarStoryFormTabs>(tabItems[0]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [tabErrors, setTabErrors] = useState({
        About: { errors: false, completed: false },
        Story: { errors: false, completed: false },
        Upload: { errors: false, completed: false },
    });

    // Add a useRef hook to the component
    const tabsContainerRef = useRef<HTMLDivElement>(null);

    const currentTabIndex = tabItems.findIndex((tab) => tab.title === activeTab.title);

    //Trigger only validation for the fields in the active tab
    const activeFieldsTrigger = async (fieldNames: string[]) => {
        const validationPromises = fieldNames.map((fieldName) => {
            return form.trigger(fieldName as keyof typeof StarStoryFormSchema.shape);
        });

        await Promise.all(validationPromises);
    };

    const activeTabFields = Object.keys(activeTab?.inputFields || {});
    const isUploadTab = activeTab?.title === IStarStoryFormTabsTitles.Upload;

    //Sets the tab status to completed if there are no errors in the active tab
    const checkTabStatus = async () => {
        const errorsArray = Object.keys(form.formState.errors);
        if (activeTabFields.every((item) => !errorsArray.includes(item))) {
            setTabErrors((prevState) => ({
                ...prevState,
                [activeTab.title]: { errors: false, completed: true },
            }));
        } else {
            setTabErrors((prevState) => ({
                ...prevState,
                [activeTab.title]: { errors: true, completed: false },
            }));
        }
    };

    const triggerToastError = () =>
        toast({
            description: `❌ Sorry, there was an error sending your story - contact us directly at ${process.env.NEXT_PUBLIC_ENQUIRY_EMAIL}`,
        });

    const createUpload = useCallback(async () => {
        try {
            return fetch('/api/uploads', {
                method: 'POST',
            })
                .then((res) => res.json())
                .then(({ id, url }) => {
                    return { id, url };
                });
        } catch (e) {
            console.error('Error in createUpload', e);
            triggerToastError();
        }
    }, []);

    const getUploadAssetId = useCallback(async (id: string) => {
        try {
            return fetch(`/api/uploads/${id}`, {
                method: 'GET',
            })
                .then((res) => res.json())
                .then((asset) => asset);
        } catch (e) {
            console.error('Error in getUploadAssetId', e);
            triggerToastError();
        }
    }, []);

    const onSubmit = useCallback(
        async (values: StarStoryFormData) => {
            setIsSubmitting(true);
            setIsModalOpen(true);

            try {
                const { images, videos, ...data } = values;
                const formData = new FormData();

                let videos_mux_asset_ids: string[] = [];
                let videos_mux_playback_ids: string[] = [];

                if (videos) {
                    for (const video of videos) {
                        const res = await createUpload();
                        if (!res) {
                            return;
                        }

                        const upload = UpChunk.createUpload({
                            endpoint: res?.url ?? '',
                            file: video.file,
                        });

                        await new Promise((resolve, reject) => {
                            upload.on('success', resolve);
                            upload.on('error', reject);
                        });

                        const asset = await getUploadAssetId(res.id);
                        if (!asset) {
                            return;
                        }

                        videos_mux_asset_ids = [...videos_mux_asset_ids, asset.asset_id];
                        videos_mux_playback_ids = [...videos_mux_playback_ids, asset.playback_id];
                    }
                }

                data.videos_mux_asset_ids = videos_mux_asset_ids;
                data.videos_mux_playback_ids = videos_mux_playback_ids;

                formData.append(
                    'data',
                    JSON.stringify({
                        data,
                        locale: router.locale === ILocales.en_AU ? I18nLocales.au : I18nLocales.nz,
                    })
                );

                //Files in strapi must have the 'files' prefix
                images?.forEach((image) => {
                    formData.append('files.images', image.file);
                });

                const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_BACKEND_URL}/api/story-submissions`, {
                    method: 'POST',
                    body: formData,
                    signal: controller.signal,
                });
                if (!response.ok) {
                    triggerToastError();
                    throw new Error('Network response was not ok');
                } else {
                    setSubmissionSuccess(true);
                    setIsModalOpen(false);
                    toast({
                        description: '✅ Your story has been submitted',
                    });
                }
            } catch (error) {
                setIsModalOpen(false);
                triggerToastError();
            } finally {
                setIsSubmitting(false);
            }
        },
        [router.locale, controller.signal, createUpload, getUploadAssetId, setSubmissionSuccess]
    );

    const handleModalClose = () => {
        // Cancel the form submission
        controller.abort();
        setIsSubmitting(false);
        setIsModalOpen(false);

        // Create a new controller so that the user can resubmit the form if they accidentally closed the modal
        setController(new AbortController());
    };

    return (
        <>
            <Root open={isModalOpen} onOpenChange={setIsModalOpen}>
                <Portal>
                    <Overlay className="fixed inset-0 bg-black bg-opacity-90 z-50 data-[state=open]:animate-overlayShow" />
                    <Content className="text-primary data-[state=open]:animate-contentShow fixed z-50 top-[50%] left-[50%] w-[80vw] translate-x-[-50%] translate-y-[-50%] bg-shell-25 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none rounded-2xl">
                        <Container className="text-center py-[140px]">
                            <h2 className="mb-10">Your Star Story is in progress</h2>
                            <div className="flex justify-center items-center space-x-2.5 p-5 mb-8">
                                <div className="bg-primary w-2.5 h-2.5 rounded-full animate-dot-1" />
                                <div className="bg-primary w-2.5 h-2.5 rounded-full animate-dot-2" />
                                <div className="bg-primary w-2.5 h-2.5 rounded-full animate-dot-3" />
                                <div className="bg-primary w-2.5 h-2.5 rounded-full animate-dot-4" />
                            </div>
                            <p className="font-medium mb-5">Upload in progress. Please do not close your browser window.</p>
                            <p>Depending on the size of the file or the speed of your internet connection, the process may take some time.</p>
                        </Container>
                        <Close asChild>
                            <div className={'fill-white absolute z-[999] -top-4 -right-4'} aria-label="close" role="button" onClick={handleModalClose}>
                                <Icon id="cross" className="w-4 h-4" />
                            </div>
                        </Close>
                    </Content>
                </Portal>
            </Root>
            <div id="submit-star-story">
                <div className="flex items-center justify-center mb-12 sm:mb-20" ref={tabsContainerRef}>
                    <div className="bg-shell text-primary rounded-3xl sm:w-auto w-full sm:rounded-full flex sm:flex-row items-center justify-center gap-2 p-1 sm:p-2 text-center">
                        {tabItems.map((tab, i) => {
                            const isActive = activeTab?.title === tab.title;
                            return (
                                <Button
                                    key={i}
                                    variant={isActive ? 'fill' : !activeTab && i === 0 ? 'fill' : 'ghost'}
                                    colour={isActive ? 'white' : !activeTab && i === 0 ? 'white' : 'primary'}
                                    id={i.toString()}
                                    disabled={isActive ? true : false}
                                    size="lg"
                                    onClick={() =>
                                        activeFieldsTrigger(activeTabFields).then(() => {
                                            checkTabStatus().then(() => {
                                                setActiveTab(tab);
                                            });
                                        })
                                    }
                                    className="flex-col sm:flex-row w-full sm:w-auto sm:items-center sm:justify-start rounded-full flex-1 gap-1 sm:gap-2.5  py-1 sm:py-6 px-6.5 sm:px-10 lg:px-20 group disabled:opacity-100"
                                >
                                    <CircleTick isCompleted={tabErrors[tab.title].completed} />
                                    <div className="flex items-center gap-1">
                                        <h6 className="text-sm sm:text-base ">{tab.title} </h6>
                                        {tabErrors[tab.title].errors && Object.keys(errors).length > 0 && <Icon id={'circle'} className="sm:w-2.5 sm:h-2.5 w-1.5 h-1.5" />}
                                    </div>
                                </Button>
                            );
                        })}
                    </div>
                </div>
                {activeTab?.tabDescription && (
                    <div className="flex max-w-md w-full mx-auto mb-12 sm:mb-20">
                        <div className="relative flex flex-col items-center justify-center max-w-md mx-auto text-primary text-center">
                            <p className="max-w-sm w-full mx-auto large">{activeTab.tabDescription}</p>
                        </div>
                    </div>
                )}

                <Form {...form}>
                    <form>
                        <div className={isUploadTab ? `justify-center items-center mx-auto max-w-sm` : 'w-full'}>
                            {Object.entries(activeTab?.inputFields || {}).map(([inputName, inputConfig], index) => {
                                const fieldName = inputName as keyof typeof StarStoryFormSchema.shape;
                                switch (activeTab.title) {
                                    case IStarStoryFormTabsTitles.About:
                                        return TabFields({
                                            form: form,
                                            fieldName: fieldName,
                                            inputConfig: inputConfig,
                                            className: `${index < 2 ? 'sm:inline-block sm:w-1/2' : 'block'} ${index >= 2 ? 'w-full' : ''} ${index === 1 && 'sm:pl-2'} `,
                                            inputType: 'text',
                                        });

                                    case IStarStoryFormTabsTitles.Story:
                                        return TabFields({
                                            form: form,
                                            fieldName: fieldName,
                                            inputConfig: inputConfig,
                                            className: 'block',
                                            inputType: 'textarea',
                                        });

                                    case IStarStoryFormTabsTitles.Upload:
                                        return UploadTabFields({
                                            form: form,
                                            fieldName: fieldName,
                                            inputConfig: inputConfig,
                                        });
                                }
                            })}
                        </div>
                        {isUploadTab && termsAndConditions && (
                            <div className="flex justify-center items-center text-center my-12 ">
                                <RichText className={'[&>p]:text-xs !max-w-sm'} content={termsAndConditions as string} />
                            </div>
                        )}
                        {Object.keys(errors).length > 0 && currentTabIndex === 2 && (
                            <p className="flex justify-center text-red-600 text-center">*Required fields need attention in order to proceed*</p>
                        )}
                        <div className="grid grid-cols-2 gap-6">
                            <div className="flex justify-center space-x-6 col-span-2 mt-12">
                                {currentTabIndex !== 0 && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        uppercase
                                        className="w-32"
                                        onClick={() => {
                                            setActiveTab(tabItems[currentTabIndex - 1]);
                                            if (tabsContainerRef?.current) {
                                                window.scrollTo({
                                                    top: tabsContainerRef.current.offsetTop,
                                                });
                                            }
                                        }}
                                    >
                                        PREVIOUS
                                    </Button>
                                )}

                                <Button
                                    // Trigger the validation on the tab, if not the last tab switch tabs, otherwise if it is, check for errors and submit
                                    onClick={() => {
                                        activeFieldsTrigger(activeTabFields).then(() => {
                                            checkTabStatus().then(() => {
                                                currentTabIndex !== 2
                                                    ? setActiveTab(tabItems[currentTabIndex + 1])
                                                    : Object.keys(errors).length === 0
                                                    ? onSubmit(form.getValues())
                                                    : setIsSubmitting(false);

                                                if (tabsContainerRef?.current) {
                                                    window.scrollTo({
                                                        top: tabsContainerRef.current.offsetTop,
                                                    });
                                                }
                                            });
                                        });
                                    }}
                                    variant="outline"
                                    uppercase
                                    className="w-32"
                                    disabled={isSubmitting || (Object.keys(errors).length > 0 && currentTabIndex === 2)}
                                    type="button"
                                >
                                    {isSubmitting ? 'Submitting' : currentTabIndex === 2 ? 'Submit' : 'Continue'}
                                </Button>
                            </div>
                        </div>
                    </form>
                </Form>
            </div>
        </>
    );
};
